/**
 * Planning — calendrier des interventions, deux vues commutables :
 * - « Semaine » : 7 colonnes (lundi → dimanche), une carte par intervention.
 * - « 30 jours » : grille calendrier de 6 semaines (alignée lundi → dimanche)
 *   couvrant largement les 30 prochains jours ; jours passés grisés.
 * Navigation période précédente / aujourd'hui / suivante. Chaque intervention
 * se place dans son jour, triée par heure ; plusieurs techniciens/partenaires
 * au même créneau = plusieurs cartes. Cliquer ouvre le dossier. Le calendrier
 * reste affiché même sans intervention.
 */
import { useQuery } from '@tanstack/react-query';
import { ChevronLeft, ChevronRight } from 'lucide-react';
import { useState } from 'react';
import { Link } from 'react-router-dom';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
} from '@/components/ui/card';
import { Tabs, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { DashboardLayout } from '@/components/dashboard/dashboard-layout';
import {
  listInterventions,
  type Intervention,
} from '@/features/appointments/appointments-api';
import { appointmentStatusBadge } from '@/features/appointments/appointment-labels';
import { executorLabel } from './admin-interventions-page';

type PlanningView = 'week' | 'month';

/** Nombre de jours affichés selon la vue (30 jours = grille de 6 semaines). */
const DAY_COUNT: Record<PlanningView, number> = { week: 7, month: 42 };

/** Clé jour locale (yyyy-mm-dd) pour aligner intervention et colonne. */
function dayKey(d: Date): string {
  return d.toLocaleDateString('en-CA');
}

/** Lundi 00:00 de la semaine contenant `d`. */
function startOfWeek(d: Date): Date {
  const x = new Date(d);
  const offset = (x.getDay() + 6) % 7; // lundi = 0
  x.setDate(x.getDate() - offset);
  x.setHours(0, 0, 0, 0);
  return x;
}

function timeOf(iso: string | null): string {
  if (!iso) return '';
  return new Date(iso).toLocaleTimeString('fr-FR', {
    hour: '2-digit',
    minute: '2-digit',
  });
}

const WEEKDAYS = ['Lun', 'Mar', 'Mer', 'Jeu', 'Ven', 'Sam', 'Dim'];

export function AdminPlanningPage() {
  const interventions = useQuery({
    queryKey: ['admin', 'interventions'],
    queryFn: listInterventions,
  });

  const [view, setView] = useState<PlanningView>('month');
  const [gridStart, setGridStart] = useState(() => startOfWeek(new Date()));
  const todayKey = dayKey(new Date());

  const dayCount = DAY_COUNT[view];
  const days = Array.from({ length: dayCount }, (_, i) => {
    const d = new Date(gridStart);
    d.setDate(d.getDate() + i);
    return d;
  });
  const rangeEnd = days[dayCount - 1]!;

  const byDay = new Map<string, Intervention[]>();
  for (const it of interventions.data ?? []) {
    if (!it.scheduledAt) continue;
    const key = dayKey(new Date(it.scheduledAt));
    const bucket = byDay.get(key);
    if (bucket) bucket.push(it);
    else byDay.set(key, [it]);
  }

  /** Décale d'un bloc entier (1 semaine ou 6 semaines), alignement préservé. */
  const shift = (blocks: number) =>
    setGridStart((prev) => {
      const d = new Date(prev);
      d.setDate(d.getDate() + blocks * dayCount);
      return d;
    });

  const isWeek = view === 'week';
  const rangeLabel = `${gridStart.toLocaleDateString('fr-FR', {
    day: 'numeric',
    month: 'long',
  })} – ${rangeEnd.toLocaleDateString('fr-FR', {
    day: 'numeric',
    month: 'long',
    year: 'numeric',
  })}`;

  return (
    <DashboardLayout variant="admin" title="Planning">
      <Card>
        <CardHeader className="flex flex-row flex-wrap items-center justify-between gap-3">
          <CardTitle>Planning des interventions</CardTitle>
          <div className="flex flex-wrap items-center gap-2">
            <Tabs
              value={view}
              onValueChange={(v) => {
                setView(v as PlanningView);
                setGridStart(startOfWeek(new Date()));
              }}
            >
              <TabsList>
                <TabsTrigger value="week">Semaine</TabsTrigger>
                <TabsTrigger value="month">30 jours</TabsTrigger>
              </TabsList>
            </Tabs>
            <span className="text-sm capitalize text-gray-600">{rangeLabel}</span>
            <Button
              variant="outline"
              size="sm"
              onClick={() => shift(-1)}
              aria-label="Période précédente"
            >
              <ChevronLeft className="h-4 w-4" />
            </Button>
            <Button
              variant="outline"
              size="sm"
              onClick={() => setGridStart(startOfWeek(new Date()))}
            >
              Aujourd'hui
            </Button>
            <Button
              variant="outline"
              size="sm"
              onClick={() => shift(1)}
              aria-label="Période suivante"
            >
              <ChevronRight className="h-4 w-4" />
            </Button>
          </div>
        </CardHeader>
        <CardContent>
          {!isWeek && (
            <div className="mb-2 grid grid-cols-7 gap-2 max-sm:min-w-[760px]">
              {WEEKDAYS.map((label) => (
                <div
                  key={label}
                  className="text-center text-xs font-semibold text-gray-500"
                >
                  {label}
                </div>
              ))}
            </div>
          )}
          <div className="grid grid-cols-7 gap-2 max-sm:min-w-[760px]">
            {days.map((d, i) => {
              const key = dayKey(d);
              const items = (byDay.get(key) ?? []).sort((a, b) =>
                (a.scheduledAt ?? '').localeCompare(b.scheduledAt ?? ''),
              );
              const isToday = key === todayKey;
              const isPast = key < todayKey;
              return (
                <div
                  key={key}
                  className={`rounded-md border ${
                    isToday
                      ? 'border-bcl-blue bg-bcl-blue/5'
                      : isPast && !isWeek
                        ? 'border-dashed bg-gray-50/60'
                        : 'bg-gray-50'
                  }`}
                >
                  <div
                    className={`border-b px-2 py-1.5 text-center text-xs font-semibold ${
                      isToday
                        ? 'text-bcl-blue'
                        : isPast && !isWeek
                          ? 'text-gray-400'
                          : 'text-gray-600'
                    }`}
                  >
                    {isWeek ? `${WEEKDAYS[i % 7]} ${d.getDate()}` : d.getDate()}
                  </div>
                  <div
                    className={`space-y-1.5 p-1.5 ${
                      isWeek
                        ? 'min-h-[120px]'
                        : 'min-h-[88px] max-h-[160px] overflow-y-auto'
                    }`}
                  >
                    {items.map((it) => (
                      <Link
                        key={it.id}
                        to={`/admin/projects/${it.projectId}`}
                        className="block rounded border bg-white p-2 text-xs transition hover:border-gray-400 hover:shadow-sm"
                      >
                        <div className="flex items-center justify-between gap-1">
                          <span className="font-semibold tabular-nums">
                            {timeOf(it.scheduledAt)}
                          </span>
                          <Badge
                            variant="outline"
                            className={`px-1 py-0 text-[10px] ${appointmentStatusBadge[it.status].cls}`}
                          >
                            {appointmentStatusBadge[it.status].label}
                          </Badge>
                        </div>
                        <div className="mt-0.5 truncate font-medium">
                          {it.clientName ?? 'Client'}
                        </div>
                        <div className="truncate text-[11px] text-gray-500">
                          {executorLabel(it)}
                        </div>
                      </Link>
                    ))}
                  </div>
                </div>
              );
            })}
          </div>
        </CardContent>
      </Card>
    </DashboardLayout>
  );
}
